import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { HttpService } from './http.service';
import { URLUtils } from '../urlUtils';

/**
 * Single source of truth for the "Available for Consultations" toggle.
 *
 * `is_marketplace_listed` always comes from the login/profile API response
 * (see initFromLoginResponse). This service never hardcodes a value and
 * never calls the visibility endpoint on init — only when the user
 * explicitly flips the toggle.
 */
@Injectable({ providedIn: 'root' })
export class MarketplaceVisibilityService {

  private static readonly STORAGE_KEY = 'is_marketplace_listed';

  private readonly _isListed$ = new BehaviorSubject<boolean>(this.readInitialState());
  readonly isListed$ = this._isListed$.asObservable();

  // Lets the Profile/Appointments module alerts ask the top-bar toggle
  // to scroll into view and pulse, without introducing new global state.
  private readonly _highlightRequest$ = new Subject<void>();
  readonly highlightRequest$ = this._highlightRequest$.asObservable();

  constructor(private httpService: HttpService) {}

  get isListed(): boolean {
    return this._isListed$.value;
  }

  /**
   * Call once, right after login/profile load, with the
   * `is_marketplace_listed` flag from that API response.
   * No additional API call is made here.
   */
  initFromLoginResponse(isMarketplaceListed: boolean): void {
    this.persistAndEmit(this.toBool(isMarketplaceListed));
  }

  /**
   * Persists the new value to the backend. Callers must only invoke
   * setListed(...) after this succeeds — do not update state optimistically.
   */
  updateVisibility(isMarketplaceListed: boolean) {
    return this.httpService.sendPostRequest(URLUtils.marketplaceVisibility, {
      is_marketplace_listed: isMarketplaceListed
    });
  }

  /** Updates local state (and localStorage) after a confirmed API success. */
  setListed(isMarketplaceListed: boolean): void {
    this.persistAndEmit(this.toBool(isMarketplaceListed));
  }

  /** Asks the top-bar toggle to scroll into view and briefly pulse. */
  requestToggleHighlight(): void {
    this._highlightRequest$.next();
  }

  private persistAndEmit(value: boolean): void {
    localStorage.setItem(MarketplaceVisibilityService.STORAGE_KEY, String(value));
    this._isListed$.next(value);
  }

  private readInitialState(): boolean {
    const stored = localStorage.getItem(MarketplaceVisibilityService.STORAGE_KEY);
    // Default true only until the login response seeds a real value.
    return stored === null ? true : stored === 'true';
  }

  /**
   * Safely coerces the incoming flag to a real boolean.
   *
   * `!!value` alone is not safe here: if `is_marketplace_listed` ever arrives
   * as the STRING "false" (e.g. inconsistent API serialization) rather than
   * the boolean `false`, `!!"false"` evaluates to `true` — any non-empty
   * string is truthy in JS. That silently flips the toggle ON even though
   * the underlying value is logically false. Strings are parsed explicitly;
   * everything else falls back to normal boolean coercion.
   */
  private toBool(value: unknown): boolean {
    if (typeof value === 'string') {
      return value.trim().toLowerCase() === 'true';
    }
    return !!value;
  }
}